import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from 'react';
import { AntDrawer } from '@/components/antd/drawer';
import { AntButton } from '@/components/antd';
import { Input, DatePicker, Select, message, Modal } from 'antd';
import {
    RegionSelector,
    IndustrySelector,
    PositionSelector,
} from '@/components/common';
import { RegionSelectorValue } from '@/components/common';
import moment from 'moment';
import { defaultAxios } from '@/utils';
import './report-create.less';

const { RangePicker } = DatePicker;

export interface INewReportDrawerProps {
    cid: number | string; // 客户详情页公司的CID
    visible: boolean;
    onClose: () => void;
    onSubmit: (data: INewReportData) => void | Promise<Boolean>;
    loading?: boolean;
    customerCid?: string; // 客户详情页公司的CID
    customerName?: string; // 客户公司简称
    teamData?: any[]; // 团队数据
    initialData?: Partial<INewReportData> | null;
}

export interface INewReportData {
    reportName: string;
    reviewCompanyCid: string | number;
    reviewCompanyTeam: string[];
    reviewCompanyTeamNames: string[];
    reviewTimePeriod: [moment.Moment, moment.Moment];
    comparisonTimePeriod: [moment.Moment, moment.Moment];
    competitorCompanyCids: string[];
    positionCities: string[]; // 每个职位一个字符串，职位间由;连接，职位内由,连接
    positionProfessions: string[]; // 每个职位一个字符串，职位间由;连接，职位内由,连接
    positions: string[];
}

const splitLinkedField = (value: string): string[] =>
    value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);

const sanitizeStringArray = (list: unknown[]): string[] =>
    list
        .map(item => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);

const normalizeCompetitorCids = (list: unknown): string[] => {
    const source = Array.isArray(list) ? list : [];
    return Array.from({ length: 5 }, (_, index) => {
        const item = source[index];
        if (typeof item === 'string') {
            return item.trim();
        }
        if (typeof item === 'number') {
            return String(item).trim();
        }
        return '';
    });
};

const buildLinkedSelectionMap = (
    positions: string[],
    values: string[],
): Record<string, string[]> => {
    const map: Record<string, string[]> = {};
    positions.forEach((position, index) => {
        const key = position.trim();
        if (!key) {
            return;
        }
        const current = values[index];
        if (typeof current === 'string' && current.trim()) {
            map[key] = splitLinkedField(current);
        } else {
            map[key] = [];
        }
    });
    return map;
};

const serializeLinkedSelections = (
    positions: string[],
    selectionMap: Record<string, string[]>,
    formatter?: (items: string[]) => string[],
): string[] =>
    positions.map(position => {
        const key = position.trim();
        if (!key) {
            return '';
        }
        const rawItems = selectionMap[key] || [];
        const formattedItems = formatter
            ? formatter(rawItems)
            : sanitizeStringArray(rawItems);
        return formattedItems
            .map(item => item.trim())
            .filter(Boolean)
            .join(',');
    });

const NewReportDrawer: React.FC<INewReportDrawerProps> = ({
    cid,
    visible,
    onClose,
    onSubmit,
    loading = false,
    teamData = [],
    customerName = '',
    initialData = null,
}) => {
    const createDefaultTimeRange = (): [moment.Moment, moment.Moment] => {
        const endDate = moment()
            .subtract(1, 'day')
            .startOf('day');
        const startDate = endDate.clone().subtract(1, 'year');
        return [startDate, endDate];
    };

    const createDefaultComparisonTimeRange = (): [
        moment.Moment,
        moment.Moment,
    ] => {
        const endDate = moment()
            .subtract(1, 'year')
            .subtract(1, 'day')
            .startOf('day');
        const startDate = endDate.clone().subtract(1, 'year');
        return [startDate, endDate];
    };
    const createDefaultFormData = useCallback(
        (): INewReportData => ({
            reportName: '',
            reviewCompanyCid: cid,
            reviewCompanyTeam: [],
            reviewCompanyTeamNames: [],
            reviewTimePeriod: createDefaultTimeRange(),
            comparisonTimePeriod: createDefaultComparisonTimeRange(),
            competitorCompanyCids: ['', '', '', '', ''],
            positionCities: [],
            positionProfessions: [],
            positions: [],
        }),
        [cid],
    );

    const normalizeInitialData = useCallback(
        (data?: Partial<INewReportData> | null): INewReportData => {
            const defaultData = createDefaultFormData();
            if (!data) {
                return defaultData;
            }

            return {
                ...defaultData,
                ...data,
                competitorCompanyCids: normalizeCompetitorCids(
                    data.competitorCompanyCids,
                ),
                positions: Array.isArray(data.positions)
                    ? sanitizeStringArray(data.positions)
                    : defaultData.positions,
                positionCities: Array.isArray(data.positionCities)
                    ? data.positionCities.map(item =>
                          typeof item === 'string' ? item : '',
                      )
                    : defaultData.positionCities,
                positionProfessions: Array.isArray(data.positionProfessions)
                    ? data.positionProfessions.map(item =>
                          typeof item === 'string' ? item : '',
                      )
                    : defaultData.positionProfessions,
            };
        },
        [createDefaultFormData],
    );
    const initialFormData = useMemo(
        () => normalizeInitialData(initialData),
        [normalizeInitialData, initialData],
    );
    const [formData, setFormData] = useState<INewReportData>(initialFormData);
    const [positionCityMap, setPositionCityMap] = useState<
        Record<string, string[]>
    >(() =>
        buildLinkedSelectionMap(
            initialFormData.positions,
            initialFormData.positionCities,
        ),
    );
    const [positionProfessionMap, setPositionProfessionMap] = useState<
        Record<string, string[]>
    >(() =>
        buildLinkedSelectionMap(
            initialFormData.positions,
            initialFormData.positionProfessions,
        ),
    );

    // 当客户信息变化时更新表单数据
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            reviewCompanyCid: cid,
        }));
    }, [cid]);

    const [
        competitorCidValidationMap,
        setCompetitorCidValidationMap,
    ] = useState<Record<number, boolean | undefined>>({});
    const initializeForm = useCallback(
        (data?: Partial<INewReportData> | null) => {
            const nextFormData = normalizeInitialData(data);
            setFormData(nextFormData);
            setPositionCityMap(
                buildLinkedSelectionMap(
                    nextFormData.positions,
                    nextFormData.positionCities,
                ),
            );
            setPositionProfessionMap(
                buildLinkedSelectionMap(
                    nextFormData.positions,
                    nextFormData.positionProfessions,
                ),
            );
            setCompetitorCidValidationMap({});
        },
        [normalizeInitialData],
    );
    const resetForm = useCallback(() => {
        initializeForm();
    }, [initializeForm]);
    const previousVisibleRef = useRef<boolean>(visible);
    const previousInitialDataRef = useRef<Partial<INewReportData> | null>(null);

    useEffect(() => {
        if (visible && !previousVisibleRef.current) {
            initializeForm(initialData);
        }
        if (previousVisibleRef.current && !visible) {
            resetForm();
        }
        previousVisibleRef.current = visible;
    }, [visible, initializeForm, resetForm, initialData]);

    useEffect(() => {
        if (!visible) {
            previousInitialDataRef.current = initialData;
            return;
        }
        if (previousInitialDataRef.current !== initialData) {
            initializeForm(initialData);
            previousInitialDataRef.current = initialData;
        }
    }, [initialData, initializeForm, visible]);

    // 自动生成报告名称
    useEffect(() => {
        if (formData.reviewTimePeriod[0] && formData.reviewTimePeriod[1]) {
            const startDate = formData.reviewTimePeriod[0].format(
                'YYYY年M月D日',
            );
            const endDate = formData.reviewTimePeriod[1].format('YYYY年M月D日');
            const reportName = `${customerName}复盘报告_${startDate}-${endDate}`;
            setFormData(prev => ({
                ...prev,
                reportName,
            }));
        }
    }, [formData.reviewTimePeriod, customerName]);

    // 验证时间周期
    const validateTimePeriod = (
        startDate: moment.Moment,
        endDate: moment.Moment,
    ) => {
        const normalizedStart = startDate.clone().startOf('day');
        const normalizedEnd = endDate.clone().startOf('day');
        const yesterday = moment()
            .subtract(1, 'day')
            .startOf('day');

        if (normalizedEnd.isAfter(yesterday)) {
            message.warning('时间周期最晚只能选择到昨天');
            return false;
        }

        const minEnd = normalizedStart
            .clone()
            .add(1, 'month')
            .subtract(1, 'day');
        if (normalizedEnd.isBefore(minEnd)) {
            message.warning('时间周期最小为1个月');
            return false;
        }

        const maxEnd = normalizedStart.clone().add(1, 'year');
        if (normalizedEnd.isAfter(maxEnd)) {
            message.warning('时间周期最大为1年');
            return false;
        }
        return true;
    };

    // 验证时间周期是否重合
    const validateTimePeriodOverlap = (
        reviewPeriod: [moment.Moment, moment.Moment],
        comparisonPeriod: [moment.Moment, moment.Moment],
    ) => {
        if (
            !reviewPeriod[0] ||
            !reviewPeriod[1] ||
            !comparisonPeriod[0] ||
            !comparisonPeriod[1]
        ) {
            return true; // 如果任一周期未完整选择，则不进行重合验证
        }

        const reviewStart = reviewPeriod[0].clone().startOf('day');
        const reviewEnd = reviewPeriod[1].clone().startOf('day');
        const comparisonStart = comparisonPeriod[0].clone().startOf('day');
        const comparisonEnd = comparisonPeriod[1].clone().startOf('day');

        // 检查两个时间周期是否完全相等
        const isIdentical =
            reviewStart.isSame(comparisonStart) &&
            reviewEnd.isSame(comparisonEnd);

        if (isIdentical) {
            message.warning('复盘时间和对比时间重合');
            return false;
        }

        return true;
    };

    const handleTimePeriodChange = (
        dates: any,
        type: 'review' | 'comparison',
    ) => {
        if (dates && dates[0] && dates[1]) {
            if (!validateTimePeriod(dates[0], dates[1])) {
                return;
            }
        }

        // 先更新表单数据
        const newFormData = {
            ...formData,
            [type === 'review'
                ? 'reviewTimePeriod'
                : 'comparisonTimePeriod']: dates as [
                moment.Moment,
                moment.Moment,
            ],
        };

        // 验证时间周期重合（使用更新后的数据）
        if (dates && dates[0] && dates[1]) {
            if (
                !validateTimePeriodOverlap(
                    newFormData.reviewTimePeriod,
                    newFormData.comparisonTimePeriod,
                )
            ) {
                return;
            }
        }

        setFormData(newFormData);
    };

    const handleCompetitorCidChange = (value: string, index: number) => {
        // 只允许数字
        const numericValue = value.replace(/[^0-9]/g, '');
        const newCids = [...formData.competitorCompanyCids];
        newCids[index] = numericValue;
        setFormData({
            ...formData,
            competitorCompanyCids: newCids,
        });
        setCompetitorCidValidationMap(prev => {
            if (prev[index] === undefined) {
                return prev;
            }
            return {
                ...prev,
                [index]: undefined,
            };
        });
    };

    const validateCompetitorCid = async (
        index: number,
        options: { showSuccessMessage?: boolean } = {},
    ): Promise<boolean> => {
        const { showSuccessMessage = true } = options;
        const cid = (formData.competitorCompanyCids[index] || '').trim();
        if (!cid) {
            setCompetitorCidValidationMap(prev => ({
                ...prev,
                [index]: undefined,
            }));
            return true;
        }

        const [start, end] = formData.reviewTimePeriod || [];
        if (!start || !end) {
            message.warning('请先选择复盘时间周期');
            setCompetitorCidValidationMap(prev => ({
                ...prev,
                [index]: false,
            }));
            return false;
        }

        const [resp, error] = await defaultAxios.get('/crm/report/isCidValid', {
            uid: 62913,
            cid,
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD'),
        });

        if (error) {
            setCompetitorCidValidationMap(prev => ({
                ...prev,
                [index]: false,
            }));
            return false;
        }

        const { had_team: hadTeam, had_valid_data: hadValidData } =
            resp?.data ?? {};
        const isValid = Boolean(hadTeam && hadValidData);

        if (!hadTeam) {
            message.warning(`竞对${index + 1}缺少团队数据，请确认`);
        }

        if (!hadValidData) {
            message.warning(`竞对${index + 1}在所选周期内无有效数据`);
        }

        setCompetitorCidValidationMap(prev => ({
            ...prev,
            [index]: isValid,
        }));

        if (isValid && showSuccessMessage) {
            message.success(`竞对${index + 1}校验通过`);
        }

        return isValid;
    };

    const handleSubmit = async () => {
        if (formData.reviewCompanyTeam.length === 0) {
            message.warning('请选择复盘公司团队');
            return;
        }
        if (!formData.reviewTimePeriod[0] || !formData.reviewTimePeriod[1]) {
            message.warning('请选择复盘时间周期');
            return;
        }
        // if (
        //     !formData.comparisonTimePeriod[0] ||
        //     !formData.comparisonTimePeriod[1]
        // ) {
        //     message.warning('请选择对比时间周期');
        //     return;
        // }

        // 验证时间周期重合
        if (
            !validateTimePeriodOverlap(
                formData.reviewTimePeriod,
                formData.comparisonTimePeriod,
            )
        ) {
            return;
        }

        // 验证竞对公司CID至少填写一个
        const hasCompetitorCid = formData.competitorCompanyCids.some(
            cid => (cid?.trim?.() ?? '') !== '',
        );
        if (!hasCompetitorCid) {
            message.warning('请至少填写一个竞对公司CID');
            return;
        }

        // 验证职位选择至少填写一个
        // const hasPosition = formData.positions.some(
        //     position => position.trim() !== '',
        // );
        // if (!hasPosition) {
        //     message.warning('请至少选择一个职位');
        //     return;
        // }
        // const specialCharRegex = /^[\u4e00-\u9fa5a-zA-Z]+$/;
        // const positions = formData.positions || [];
        // for (const pos of positions) {
        //     if (!pos || !pos.trim()) {
        //         continue;
        //     }
        //     if (!specialCharRegex.test(pos)) {
        //         message.error(
        //             `职位名称 "${pos}" 格式不正确，请只使用中文或英文`,
        //         );
        //         return false;
        //     }
        // }
        const competitorIndices = formData.competitorCompanyCids.reduce<
            number[]
        >((acc, currentCid, idx) => {
            if ((currentCid?.trim?.() ?? '') !== '') {
                acc.push(idx);
            }
            return acc;
        }, []);

        const validationResults = await Promise.all(
            competitorIndices.map(index => {
                if (competitorCidValidationMap[index] === true) {
                    return Promise.resolve(true);
                }
                return validateCompetitorCid(index, {
                    showSuccessMessage: false,
                });
            }),
        );

        if (validationResults.some(result => !result)) {
            message.warning('请确认所有竞对CID均已校验通过');
            return;
        }

        Modal.confirm({
            title: '确认生成报告',
            content: '是否生成报告？',
            okText: '确认',
            cancelText: '返回',
            onOk: async () => {
                const submitData = {
                    ...formData,
                    positionCities: serializeLinkedSelections(
                        formData.positions,
                        positionCityMap,
                    ),
                    positionProfessions: serializeLinkedSelections(
                        formData.positions,
                        positionProfessionMap,
                    ),
                };
                const result = await onSubmit(submitData);

                if (result !== false) {
                    resetForm();
                }

                return result;
            },
        });
    };

    const handlePositionsChange = useCallback((positions: string[]) => {
        setFormData(prev => ({
            ...prev,
            positions,
        }));
    }, []);

    useEffect(() => {
        setPositionCityMap(prev => {
            const next: Record<string, string[]> = {};
            formData.positions.forEach(position => {
                next[position] = prev[position] || [];
            });
            return next;
        });
        setPositionProfessionMap(prev => {
            const next: Record<string, string[]> = {};
            formData.positions.forEach(position => {
                next[position] = prev[position] || [];
            });
            return next;
        });
    }, [formData.positions]);

    const handlePositionReachMax = useCallback((max: number) => {
        message.warning(`最多可选择${max}个职位`);
    }, []);

    const handlePositionCitiesChange = useCallback(
        (position: string, value: RegionSelectorValue) => {
            setPositionCityMap(prev => ({
                ...prev,
                [position]: sanitizeStringArray(value),
            }));
        },
        [],
    );

    const handlePositionProfessionsChange = useCallback(
        (position: string, codes: string[]) => {
            setPositionProfessionMap(prev => ({
                ...prev,
                [position]: sanitizeStringArray(codes),
            }));
        },
        [],
    );

    const handleRegionReachMax = useCallback((max: number) => {
        message.warning(`每个职位地区最多可选择${max}个`);
    }, []);

    const fetchPositionSuggestions = useCallback(async (keyword: string) => {
        const trimmed = keyword.trim();
        if (!trimmed) {
            return [];
        }
        const [resp, error] = await defaultAxios.get(
            '/crm/report/positionSugs',
            {
                keyword: trimmed,
            },
            {
                silent: true,
            },
        );
        if (error || !Array.isArray(resp?.data)) {
            return [];
        }
        return resp.data
            .map((item: any) =>
                typeof item?.name === 'string' ? item.name.trim() : '',
            )
            .filter(Boolean);
    }, []);

    const disableDatesAfterYesterday = (current: moment.Moment | null) =>
        Boolean(current && current >= moment().startOf('day'));

    const handleCancel = () => {
        resetForm();
        onClose();
    };

    return (
        <>
            <AntDrawer
                title="新增客户复盘报告"
                placement="right"
                bodyStyle={{
                    overflow: 'scroll',
                }}
                width={650}
                onClose={handleCancel}
                visible={visible}
            >
                <div className="standard-drawer-form-wrapper">
                    <div className="new-report-form">
                        <div className="form-item">
                            <label>报告名称：</label>
                            <Input
                                value={formData.reportName}
                                disabled
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        reportName: e.target.value,
                                    })
                                }
                                placeholder="报告名称将根据时间周期自动生成"
                            />
                        </div>

                        <div className="form-item">
                            <label>复盘公司CID：</label>
                            <Input
                                value={formData.reviewCompanyCid}
                                disabled
                                placeholder="默认取客户详情页公司的CID"
                            />
                        </div>

                        <div className="form-item">
                            <label className="form-item-label">
                                <span className="form-item-required">*</span>
                                复盘团队：
                            </label>
                            <Select
                                mode="multiple"
                                placeholder="请选择团队"
                                style={{ width: '100%' }}
                                value={formData.reviewCompanyTeam}
                                onChange={(
                                    values: string[] = [],
                                    option: any,
                                ) => {
                                    const selectedNames = option.map(
                                        (opt: any) =>
                                            opt.props.children.join(''),
                                    );
                                    setFormData(prev => ({
                                        ...prev,
                                        reviewCompanyTeam: values,
                                        reviewCompanyTeamNames: selectedNames,
                                    }));
                                }}
                            >
                                {teamData
                                    .sort(
                                        (a, b) =>
                                            moment(b.end_time).valueOf() -
                                            moment(a.end_time).valueOf(),
                                    )
                                    .map(member => (
                                        <Select.Option
                                            key={member.id}
                                            value={member.id}
                                        >
                                            {member.id}-{member.team_name}-
                                            {member.adm_name}-
                                            {member.adm_mobile}-
                                            {member.end_time}
                                        </Select.Option>
                                    ))}
                            </Select>
                        </div>

                        <div className="form-item">
                            <label className="form-item-label">
                                <span className="form-item-required">*</span>
                                复盘时间周期：
                            </label>
                            <RangePicker
                                value={formData.reviewTimePeriod}
                                disabledDate={disableDatesAfterYesterday}
                                onChange={dates =>
                                    handleTimePeriodChange(dates, 'review')
                                }
                                format="YYYY/MM/DD"
                                style={{ width: '100%' }}
                            />
                            <div className="form-hint">
                                时间范围：最少1个月 最多1年
                            </div>
                        </div>

                        <div className="form-item">
                            <label>对比时间周期：</label>
                            <RangePicker
                                value={formData.comparisonTimePeriod}
                                disabledDate={disableDatesAfterYesterday}
                                onChange={dates =>
                                    handleTimePeriodChange(dates, 'comparison')
                                }
                                format="YYYY/MM/DD"
                                style={{ width: '100%' }}
                            />
                            <div className="form-hint">
                                时间范围：最少1个月 最多1年
                            </div>
                        </div>

                        <div className="form-item">
                            <label className="form-item-label">
                                <span className="form-item-required">*</span>
                                竞对公司：
                            </label>
                            <div className="competitor-inputs-row">
                                {formData.competitorCompanyCids.map(
                                    (cid, index) => (
                                        <div
                                            key={index}
                                            className="competitor-input-item-row"
                                        >
                                            <label>竞对{index + 1}：</label>
                                            <Input
                                                value={cid}
                                                onChange={e =>
                                                    handleCompetitorCidChange(
                                                        e.target.value,
                                                        index,
                                                    )
                                                }
                                                onBlur={() =>
                                                    validateCompetitorCid(index)
                                                }
                                                placeholder="仅支持数字"
                                                maxLength={20}
                                            />
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>

                        <div className="form-item">
                            <label>职位选择：</label>
                            <PositionSelector
                                value={formData.positions}
                                onChange={handlePositionsChange}
                                fetchSuggestions={fetchPositionSuggestions}
                                maxSelection={3}
                                placeholder="请选择职位（最多 3 个）"
                                onReachMax={handlePositionReachMax}
                                style={{ width: '100%' }}
                            />
                            {/* <div className="form-hint">
                                支持输入关键词自动补全，最多可选择三个职位。
                            </div> */}
                        </div>
                        {formData.positions.length > 0 ? (
                            formData.positions.map((position, index) => (
                                <div
                                    className="form-item position-linkage-item"
                                    key={position}
                                >
                                    <label>{`职位${index + 1}：${position}`}</label>
                                    <div className="position-linkage-field">
                                        <span className="position-linkage-label">
                                            地区选择：
                                        </span>
                                        <RegionSelector
                                            value={
                                                positionCityMap[position] || []
                                            }
                                            onChange={value =>
                                                handlePositionCitiesChange(
                                                    position,
                                                    value,
                                                )
                                            }
                                            placeholder="请选择城市地区"
                                            maxTagCount={4}
                                            maxSelection={10}
                                            onReachMax={handleRegionReachMax}
                                        />
                                    </div>
                                    <div className="position-linkage-field">
                                        <span className="position-linkage-label">
                                            行业选择：
                                        </span>
                                        <IndustrySelector
                                            value={
                                                positionProfessionMap[
                                                    position
                                                ] || []
                                            }
                                            onChange={codes =>
                                                handlePositionProfessionsChange(
                                                    position,
                                                    codes,
                                                )
                                            }
                                            onConfirm={codes =>
                                                handlePositionProfessionsChange(
                                                    position,
                                                    codes,
                                                )
                                            }
                                            maxSelection={10}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="form-item">
                                <label>职位关联筛选：</label>
                                <div className="form-hint">
                                    请先选择职位，再为每个职位配置地区和行业方向。
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="fixed-form-btns">
                        <AntButton
                            size="large"
                            type="primary"
                            loading={loading}
                            onClick={handleSubmit}
                        >
                            提交
                        </AntButton>
                        <AntButton size="large" onClick={handleCancel}>
                            取消
                        </AntButton>
                    </div>
                </div>
            </AntDrawer>
        </>
    );
};

export default NewReportDrawer;
